import {AnyAction, Dispatch,bindActionCreators} from 'redux';
import { ConnectedProps, connect } from 'react-redux';
import React, { useEffect, useMemo, useState } from 'react';
import ScrollBar from 'react-custom-scrollbars-4';

import {clearTsCodes, getMostRecentTsCodes, getTsCodes} from '../../../../actions/tsCodeActions';

import ApiHelper from '../../../../helpers/apiHelper';
import DateHelper from '../../../../helpers/dateHelper';
import TextInput from '../../../../common/TextInput';
import TimesheetHelper from '../../../../helpers/timesheetHelper';

import {Period} from '../../../../typescript/dateTypes';
import {StoreState, StoreTsCode, StoreTsCodes } from '../../../../typescript/storeTypes';

export const AddTimesheetCode = (props: Props & MappedProps) => {
    const [value, setValue] = useState<string>(props.value ?? '');
    const [activeTab, setActiveTab] = useState<number>(0);
    const [selectedTsCodes, setSelectedTsCodes] = useState<StoreTsCodes>([]);
    const [tabChangeRequested, setTabChangeRequested] = useState<boolean>(false);
    const [selectAllChecked, setSelectAllChecked] = useState<boolean>(false);

    const getTsCodes: (value: string) => void = useMemo(() => ApiHelper.debounce(props.getTsCodes, 300), [props.getTsCodes]);

    const onChange = (event: React.SyntheticEvent<HTMLInputElement> & { currentTarget: HTMLInputElement }): void => {
        setValue(event.currentTarget.value);
        getTsCodes(event.currentTarget.value);
    };

    const onTimesheetCodeClick = (tsCode: StoreTsCode) => {
        const tsCodeIndex = selectedTsCodes.findIndex(stc => stc.id === tsCode.id);
        const newSelectedTsCodes = selectedTsCodes.slice();
        if (tsCodeIndex === -1) {
            newSelectedTsCodes.push(tsCode);
        } else {
            newSelectedTsCodes.splice(tsCodeIndex, 1);
        }

        setSelectedTsCodes(newSelectedTsCodes);
        setSelectAllChecked(newSelectedTsCodes.length === filteredTimesheetCodes.length);
    };

    const addSelected = (): void => {
        props.addAssignments(selectedTsCodes);
    };

    const getTsCodesToUse = (): StoreTsCodes => {
        return (activeTab === 1 && props.mostRecentTsCodes.length > 0 ? props.mostRecentTsCodes : props.tsCodes);
    };
    
    const getFilteredTimesheetCodes = (): StoreTsCodes => {
        return TimesheetHelper.sortTsCodes(getTsCodesToUse()
            .filter(tsc => props.timesheetCodes.findIndex(tsc2 => tsc.id === tsc2.id) === -1)
        );
    };
    const filteredTimesheetCodes = useMemo(getFilteredTimesheetCodes, [props.tsCodes, props.mostRecentTsCodes, props.timesheetCodes, activeTab]);

    const isTabActive = (tab: number): boolean => activeTab === tab;

    const setTabActive = (tab: number): void => {
        if (tabChangeRequested) return;

        setTabChangeRequested(true);
        setActiveTab(tab);
        setSelectedTsCodes([]);
        setSelectAllChecked(false);

        switch (tab) {
            case 0: {
                if (!value) {
                    setTabChangeRequested(false);
                } else {
                    props.getTsCodes(value);
                }
                break;
            }
            case 1: {
                const month = DateHelper.addMonths(props.period.startDate, -1);
                props.getMostRecentTsCodes(props.user.impersonatedUser.username,
                    month.getMonth() + 1,
                    month.getFullYear());
                break;
            }
        }
    };

    const onSelectAll = (): void => {
        const selectedTsCodes = [];

        if (!selectAllChecked) {
            for (let i = 0, len = filteredTimesheetCodes.length; i < len; i++) {
                selectedTsCodes.push(filteredTimesheetCodes[i]);
            }
            setSelectAllChecked(true);
        }
        else {
            setSelectAllChecked(false);
        }

        setSelectedTsCodes(selectedTsCodes);
    };

    useEffect(() => {
        if (tabChangeRequested) {
            setTabChangeRequested(false);
        }

    }, [tabChangeRequested]);
    
    useEffect(() => {
        props.clearTsCodes();

        return () => {
            props.clearTsCodes();
        };
    }, []); 

    return (
        <div className="add-timesheet-code">
            <div className="timesheet-code-tab-container">
                <div className={'timesheet-code-tab' + (isTabActive(0) ? ' active' : '')}
                    onClick={() => setTabActive(0)}>
                    <div>SEARCH TIMESHEET CODE</div>
                    <div className={'line' + (isTabActive(0) ? ' black' : '')}/>
                </div>
                <div className={'timesheet-code-tab' + (isTabActive(1) ? ' active' : '')}
                        onClick={() => setTabActive(1)}>
                    <div>MY RECENT TIMESHEET CODES</div>
                    <div className={'line' + (isTabActive(1) ? ' black' : '')}/>
                </div>
            </div>
            {isTabActive(0) ?
                <div className="search-timesheetcode-container">
                    <TextInput id="searchTimesheetCode"
                        name="searchTimesheetCode"
                        onChange={(event: React.SyntheticEvent<HTMLInputElement> & { currentTarget: HTMLInputElement }) => onChange(event)}
                        autoFocus/>
                </div>
                : null
            }
            {props.tsCodes.length === parseInt(process.env.API_PAGESIZE as unknown as string) && isTabActive(0) ?
                <div className="searchResultMessage">Too many results. Please refine your search.</div>
                : null
            }
            {isTabActive(1) ?
                (filteredTimesheetCodes.length > 0 ?
                    <div className="addTimesheetCode-selectAll no-select"
                            onClick={onSelectAll}>
                        <input type="checkbox" className="addTimesheetCode-selectAll-checkbox" checked={selectAllChecked}/>
                        <div className="addTimesheetCode-selectAll-checkbox-text">SELECT ALL</div>
                    </div> : <div className="no-most-recent-tscodes">No results found</div>
                )
                : null
            }
            <div className="selected-timesheet-code-list">
                {selectedTsCodes.map(tsCode => {
                    return (<div key={`${tsCode.id}`} className="selected-timesheet-code">
                            <input type="checkbox" checked onChange={() => onTimesheetCodeClick(tsCode)}/>
                            <div className="selected-timesheet-code-container" onClick={() => onTimesheetCodeClick(tsCode)}>
                                <div className="selected-timesheet-code-name">{tsCode.name}</div>
                            </div>
                    </div>);
                })}
            </div>
            <div className="timesheet-code-list">
                <div className={'scrollbar-container-timesheet-code-list' + (filteredTimesheetCodes.length === selectedTsCodes.length ? '' : ' has-elements')}>
                    <ScrollBar>
                        {filteredTimesheetCodes.map(tsCode => {
                            return (
                                selectedTsCodes.find(t => t.id === tsCode.id) === undefined ?
                                <div key={`${tsCode.id}`} className="selectable-timesheet-code"
                                        onClick={() => onTimesheetCodeClick(tsCode)}>
                                        <input type="checkbox"
                                            checked={selectedTsCodes.find(t => t.id === tsCode.id) !== undefined}
                                            onChange={() => onTimesheetCodeClick(tsCode)}/>
                                    <div className="selectable-timesheet-code-container"
                                            onClick={() => onTimesheetCodeClick(tsCode)}>
                                        <div className="selectable-timesheet-code-name">{tsCode.name}</div>
                                        <div className="selectable-timesheet-code-description">{tsCode.description}</div>
                                    </div>
                                </div> : null
                            );
                        })}
                    </ScrollBar>
                </div>
            </div>
            <div className="add-selected">
                <input type="submit" value="ADD SELECTED"
                        className="add-timesheet-code-button"
                        onClick={addSelected}
                        disabled={selectedTsCodes.length === 0}/>
            </div>
        </div>
    );
};

function mapDispatchToProps(dispatch: Dispatch<AnyAction>) {
    return bindActionCreators({getTsCodes, getMostRecentTsCodes, clearTsCodes}, dispatch);
}

const connector = connect(mapStateToProps, mapDispatchToProps);
type Props = {
    value?: string,
    period: Period,

    addAssignments: (tsCodes: StoreTsCodes) => void
};


type MappedProps = ConnectedProps<typeof connector>;

function mapStateToProps(state: StoreState, props: Props) {
    return {
        user: state.user,
        tsCodes: state.tsCodes,
        mostRecentTsCodes: state.mostRecentTsCodes,
        timesheetCodes: state.timesheet.timesheetCodes,
    };
}

export default connector(AddTimesheetCode);
